import { useCallback, useState } from 'react';
import useHitLabGuestPresence from './useHitLabGuestPresence';

const MAX_MARKERS = 8;
const LOCAL_VISITOR_MARKER = {
  key: 'hitlab-local-visitor',
  active: true,
};

const getGuestEntries = (payload = {}) => {
  const guests = payload.guests || payload.sessions || payload.guestSessions || {};
  if (Array.isArray(guests)) return guests;
  if (guests && typeof guests === 'object') {
    return Object.entries(guests).map(([sessionId, guest]) => ({
      ...(guest || {}),
      sessionId: guest?.sessionId || guest?.session_id || sessionId,
    }));
  }
  return [];
};

const getGuestKey = (guest = {}, index = 0) =>
  String(
    guest.sessionId ||
      guest.session_id ||
      guest.guestId ||
      guest._id ||
      guest.id ||
      `guest-${index}`,
  );

const getGuestCount = (payload = {}, entries = []) => {
  const count = Number(payload.count);
  if (payload.count !== undefined && Number.isFinite(count) && count >= 0) {
    return Math.floor(count);
  }
  return entries.length;
};

const buildMarkers = (entries = []) =>
  entries.slice(0, MAX_MARKERS).map((guest, index) => ({
    key: getGuestKey(guest, index),
    active: String(guest.activityType || guest.status || '').toLowerCase() !== 'background',
  }));

const withLocalVisitorFloor = (count) =>
  Math.max(1, Number.isFinite(Number(count)) ? Math.floor(Number(count)) : 1);

const getCountFromPayload = (payload = {}) => {
  const count = Number(payload.count);
  return payload.count !== undefined && Number.isFinite(count) && count >= 0
    ? Math.floor(count)
    : null;
};

const getRemovedGuestKeys = (removed = []) => {
  if (Array.isArray(removed)) {
    return removed.map((item, index) => {
      if (typeof item === 'string') return item;
      return getGuestKey(item, index);
    });
  }
  if (removed && typeof removed === 'object') return Object.keys(removed);
  return [];
};

const trimMarkersForCount = (markers = [], count = null) => {
  const realMarkers = markers.filter(
    (marker) => marker.key !== LOCAL_VISITOR_MARKER.key,
  );
  const limit =
    count === null
      ? MAX_MARKERS
      : Math.min(MAX_MARKERS, withLocalVisitorFloor(count));
  const trimmed = realMarkers.slice(0, limit);
  return trimmed.length ? trimmed : [LOCAL_VISITOR_MARKER];
};

export default function HitLabGalaxyGatePresence() {
  const [visitorCount, setVisitorCount] = useState(1);
  const [markers, setMarkers] = useState([LOCAL_VISITOR_MARKER]);

  const handleSnapshot = useCallback((payload = {}) => {
    const entries = getGuestEntries(payload);
    const count = getGuestCount(payload, entries);
    setVisitorCount(withLocalVisitorFloor(count));
    const nextMarkers = buildMarkers(entries);
    setMarkers(trimMarkersForCount(nextMarkers, count));
  }, []);

  const handleDiff = useCallback((payload = {}) => {
    const count = getCountFromPayload(payload);
    const added = getGuestEntries({ guests: payload.add || [] });
    const updated = getGuestEntries({ guests: payload.update || [] });
    const removedKeys = getRemovedGuestKeys(payload.remove);

    setVisitorCount((current) =>
      withLocalVisitorFloor(
        count !== null ? count : Number(current) + added.length - removedKeys.length,
      ),
    );

    if (added.length || updated.length || removedKeys.length || count !== null) {
      setMarkers((current) => {
        let next = current.filter(
          (marker) =>
            marker.key !== LOCAL_VISITOR_MARKER.key &&
            !removedKeys.includes(marker.key),
        );

        updated.forEach((guest, index) => {
          const marker = buildMarkers([guest], index)[0];
          if (!marker) return;
          const existingIndex = next.findIndex((item) => item.key === marker.key);
          if (existingIndex >= 0) {
            next[existingIndex] = { ...next[existingIndex], ...marker };
          }
        });

        if (added.length) {
          const incoming = buildMarkers(added);
          const incomingKeys = incoming.map((item) => item.key);
          next = [
            ...incoming,
            ...next.filter((marker) => !incomingKeys.includes(marker.key)),
          ];
        }

        return trimMarkersForCount(next, count);
      });
    }
  }, []);

  const handleCount = useCallback((payload = {}) => {
    const count = getCountFromPayload(payload);
    if (count === null) return;
    setVisitorCount(withLocalVisitorFloor(count));
    setMarkers((current) => trimMarkersForCount(current, count));
  }, []);

  useHitLabGuestPresence({
    onCount: handleCount,
    onDiff: handleDiff,
    onSnapshot: handleSnapshot,
    source: 'hitlab_landing',
  });

  const displayVisitorCount = withLocalVisitorFloor(visitorCount);
  const visitorLabel = displayVisitorCount === 1 ? 'visitor' : 'visitors';
  const copy = `${displayVisitorCount.toLocaleString()} ${visitorLabel} at the Galaxy Gate`;

  return (
    <aside
      className="hitlab-galaxy-gate-presence"
      aria-label="Galaxy Gate visitor presence"
    >
      <div
        className="hitlab-galaxy-gate-sky"
        data-testid="hitlab-galaxy-gate-pulsar-stars"
        aria-hidden="true"
      >
        <span />
        <span />
        <span />
      </div>
      <div className="hitlab-galaxy-gate-copy">
        <span className="hitlab-galaxy-gate-dot" aria-hidden="true" />
        <div>
          <strong>{copy}</strong>
          <small>Live visitor signal from JG Universe.</small>
        </div>
      </div>
      {markers.length ? (
        <div
          className="hitlab-galaxy-gate-markers"
          aria-label="Recent Galaxy Gate visitor activity"
        >
          {markers.map((marker, index) => (
            <span
              key={`${marker.key}-${index}`}
              className={marker.active ? 'is-active' : ''}
              aria-hidden="true"
            />
          ))}
        </div>
      ) : null}
    </aside>
  );
}
